import { Button } from '@/src/common/components/Button'
import { useLogError } from '@/src/common/hooks/useLogError'
import { PaymentConfirmation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentConfirmation'
import { ScrollView } from 'react-native'
import { useAlertPaymentProcessingError } from '@/src/app/main/(tabs)/card/card-management/payment/_useAlertPaymentProcessingError'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import type { FunctionComponent } from 'react'
import { LegacyOnetimePayReview } from '@/src/experiences/card-payments/screens/OnetimePayReview/LegacyOnetimePayReview'
import { PlatformOnetimePayReview } from '@/src/experiences/card-payments/screens/OnetimePayReview/PlatformOnetimePayReview'
import {
  type OneTimePaymentStore,
  useOneTimePaymentStore,
} from '@/src/experiences/card-payments/store/useOneTimePaymentStore'
import { Routes } from '@/src/common/enums/routes'

export function useOnetimePayReviewCardStackScreenProps() {
  return usePaymentScreen(
    'oneTimePayment/confirmation',
    'Review Payment',
    '/main/card/card-management/payment/oneTimePayment/fundingSource',
  )
}

export type OnetimePayReviewComponentType = FunctionComponent<{
  createOneTimePayment?: () => void
  loading?: boolean
  OnetimePayReviewComponent: OnetimePayReviewComponentType
  onetimePaymentStore: OneTimePaymentStore
  alertPaymentProcessingError: ReturnType<typeof useAlertPaymentProcessingError>
  onetimePayReviewCardStackScreenProps: ReturnType<typeof useOnetimePayReviewCardStackScreenProps>
  logError: ReturnType<typeof useLogError>
  onSuccess: () => void
  onError: (error: unknown) => void
}>

const OnetimePayReview = provideCardPlatform<OnetimePayReviewComponentType>(
  ({ platform, ToWrap }) => {
    const alertPaymentProcessingError = useAlertPaymentProcessingError()
    const logError = useLogError()
    const onetimePayReviewCardStackScreenProps = useOnetimePayReviewCardStackScreenProps()
    const onetimePaymentStore = useOneTimePaymentStore()

    const OnetimePayReviewComponent = (() => {
      switch (platform) {
        case 'HIGHNOTE':
          return LegacyOnetimePayReview
        case 'LOANPRO':
          return PlatformOnetimePayReview
        default:
          platform satisfies undefined
          return ToWrap
      }
    })()

    return (
      <OnetimePayReviewComponent
        loading={!platform}
        OnetimePayReviewComponent={ToWrap}
        onetimePaymentStore={onetimePaymentStore}
        alertPaymentProcessingError={alertPaymentProcessingError}
        logError={logError}
        onetimePayReviewCardStackScreenProps={onetimePayReviewCardStackScreenProps}
        onSuccess={() =>
          onetimePayReviewCardStackScreenProps.routerPush(
            Routes.CardManagementOneTimePaymentSuccessful.route,
          )
        }
        onError={(error) => {
          logError(error)
          alertPaymentProcessingError()
        }}
      />
    )
  },
)(({ createOneTimePayment, loading, onetimePayReviewCardStackScreenProps: { Modal } }) =>
  Modal({
    children: (
      <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
        <PaymentConfirmation cardInformationTitle="Review Payment" />
        <Button
          title="Submit Payment"
          onPress={createOneTimePayment}
          testID={CardPaymentsE2E.Ids.Press.SubmitPayment}
          disabled={loading}
          loading={loading}
        />
      </ScrollView>
    ),
  }),
)

export const OnetimePayReviewScreen = {
  name: 'auto-pay-schedule',
  Screen: OnetimePayReview,
} as const
